import {
  Table,
  TableActionBarRow,
  Text,
} from "melco-ui";
import { useTranslation } from "react-i18next";

import { useState } from "react";

import { ControlcenterMelcoModelsMachineLicenseListItem, FormattedDate } from "melco-shared-logic";
import { useMachineLicenses } from "../../../hooks/machineLicenses/useMachineLicenses";
import { formatUTCDate } from "../../../helper/dateHelper";
import dayjs from "dayjs";
import { AsyncTooltip, TooltipBulletItem, TooltipContainer, TooltipText, TooltipTitle } from "../../tootltip/AsyncTooltip";
import { capitalize, some } from "lodash-es";
import { StatusColorMap } from "../../../types/status";
import styled from "styled-components";
import { useAccountGetPendingTransaction } from "../../../hooks/machineLicenses/useLicensesMachinePendingChanges";
import { MachineLicensesListFilter } from "./MachineLicensesListFilter";
import { MachineLicensesListFiltersType } from "../../../hooks/machineLicenses/MachineLicensesListFiltersType";

type MachineLicensesListPropTypes = {
  openGenerateLicenseDrawer?: boolean;
  setOpenGenrateLicenseDrawerFunction?: (v: boolean) => void
}

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const TooltipWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

export const MachineLicensesList: React.FC<MachineLicensesListPropTypes> = ({ openGenerateLicenseDrawer, setOpenGenrateLicenseDrawerFunction }) => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    filtersChange } = useMachineLicenses();

  const { t } = useTranslation();

  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [isFiltered, setIsFilters] = useState<boolean>(false)

  const handleToggle = (id: string | null) => {
    if (activeTooltipId === id) {
      // Clicked the same icon → close it
      setActiveTooltipId(null);
    } else {
      // Clicked another icon  open the new one

      setTimeout(() => {
        setActiveTooltipId(id);
      }, 500);
    }
  };

  const displayExpiry = (item: ControlcenterMelcoModelsMachineLicenseListItem) => {
    const now = dayjs();
    if (item.is_subscription === false) {
      return t("machinelicenses.does_not_expire");
    }

    if (item.is_subscription === true) {
      if (!item.cancelled_on) {
        return t("machinelicenses.does_not_expire");
      } else {
        if (dayjs(item.cancelled_on).isBefore(now)) {
          return t("machinelicenses.expired");
        } else {
          if (item.cancelled_on) {
            return formatUTCDate(item.cancelled_on, "L");
          }

        }
      }
    }

    /**
     *   It is fallback so that if anyhow is_subscription is not
      * coming in response , just to avoid run time exception / error
      * we will display Does not expire
     */

    return t("machinelicenses.does_not_expire");
  };

  const LicenseStatusCell = ({ item }: { item: ControlcenterMelcoModelsMachineLicenseListItem }) => {
    const { t } = useTranslation();
    const status = item?.status ?? -1;
    const color = StatusColorMap[status];
    const label = t(`machinelicenses.licenseStatus.${status}`, '');

    return <CellWrapper><Text type={color}>{label}</Text>

      {
        status == 2 && (<TooltipWrapper><AsyncTooltip
          id={(item.transaction_id ?? "")}
          param={item.transaction_id ?? ""}
          key={item.transaction_id ?? ""}
          isActive={activeTooltipId === ((item.transaction_id ?? ""))}
          onToggle={handleToggle}
          useDataHook={useAccountGetPendingTransaction}
          renderContent={(data) => {
            if (!data) return <TooltipText>{t("machinelicenses.tooltip.no_data_available")}</TooltipText>;

            const date = formatUTCDate(data.subscription_start_date, "YYYY-MM-DD");
            const old_sub_type = capitalize(data?.old_sub_type ?? "");
            const new_sub_type = capitalize(data?.new_sub_type ?? "");
            const old_license_count = data.old_license_count
            const new_license_count = data.new_license_count

            return (
              <TooltipContainer>
                <TooltipTitle>{t("machinelicenses.tooltip.changes_apply", { date })}</TooltipTitle>
                {
                  old_license_count && new_license_count && <TooltipBulletItem>
                    {t("machinelicenses.tooltip.licenses_count", { old_license_count, new_license_count })}
                  </TooltipBulletItem>
                }
                {
                  old_sub_type && new_sub_type && <TooltipBulletItem>
                    {t("machinelicenses.tooltip.billing_change", { old_sub_type, new_sub_type })}
                  </TooltipBulletItem>
                }
              </TooltipContainer>
            );
          }}
        />
        </TooltipWrapper>)

      }
    </CellWrapper>;
  };

  const columns = [
    {
      title: t("machinelicenses.list.columns.transaction_id"),
      key: "transaction_id",
      dataIndex: "transaction_id",
      sorter: true,

      width: 150
    },
    {
      title: t("machinelicenses.list.columns.amount"),
      key: "license_count",
      dataIndex: "license_count",
      sorter: true,
      width: 90
    },
    // {
    //   title: t("machinelicenses.list.columns.license_type"),
    //   key: "is_subscription",
    //   // dataIndex: "product_version_number",
    //   sorter: true,
    //   width: 80
    //   , render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => {
    //     return <>
    //       {
    //         item.is_subscription ? t("machinelicenses.list.subscription")
    //           :
    //           t("machinelicenses.list.one_time_buy")
    //       }
    //     </>
    //   }
    // },
    {
      title: t("machinelicenses.list.columns.level"),
      key: "level",
      //dataIndex: "level",
      sorter: true,
      render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => (<>{t(`machinelicenses.licenseLevel.${item.level?.toLowerCase()}`, '')}</>),
      width: 90
    },
    {
      title: t("machinelicenses.list.columns.user_name"),

      key: "user_name",
      dataIndex: "user_name",
      sorter: true,
      width: 130
    },
    {
      title: t("machinelicenses.list.columns.created"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      width: 110,
      render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("machinelicenses.list.columns.subscription_start_date"),
      key: "subscription_start_date",
      dataIndex: "subscription_start_date",
      sorter: true,
      width: 110,
      render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => (
        <>{item.subscription_start_date ? formatUTCDate(item.subscription_start_date, "L") : ""}</>
      ),
    },
    {
      title: t("machinelicenses.list.columns.renews_on"),
      key: "renewal_date",
      dataIndex: "renewal_date",
      sorter: true,
      width: 110,
      render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => (
        <> {
          item.is_subscription
            ? (
              item.cancelled_on ? "" : (item.renewal_date
                ? formatUTCDate(item.renewal_date, "L")
                : "")
            )
            : t("machinelicenses.no_renewal")
        }
        </>
      ),
    },
    {
      title: t("machinelicenses.list.columns.expires_on"),
      key: "expiration_date",
      sorter: true,
      width: 110,
      render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => (
        displayExpiry(item)
      ),
    },

    {
      title: t("machinelicenses.list.columns.status"),
      key: "status",
      //dataIndex: "status",
      sorter: true,
      width: 130,
      render: (_: string, item: ControlcenterMelcoModelsMachineLicenseListItem) => (
        <>  <LicenseStatusCell item={item} /></>
      ),

    }];


  return (
    <>

      <TableActionBarRow>


        <MachineLicensesListFilter
          onFiltersChange={(filters: MachineLicensesListFiltersType | undefined) => {
            console.log("filters", some(filters))
            // setIsFilter to check and display text message if no data comes in search
            setIsFilters(some(filters))
            filtersChange(filters)
          }}
        />
      </TableActionBarRow>

      <Table
        showSorterTooltip={false}
        sticky
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="transaction_id"
        noDataMessage={isFiltered ? t("machinelicenses.list.empty_filter_search") : t("machinelicenses.list.empty")}
        errorComponent={errorComponent}

      />



    </>
  );
};
